// ===============================================
// UPLOAD MANAGER (Singleton - Lives Outside React)
// ===============================================

const globalUppyInstances = new Map();

import {
  attachment_active_upload_session_tokens_path,
  attachment_create_upload_token_path,
  attachment_finalize_path,
  attachment_status_path,
  attachment_update_status_path,
  virus_scanner_get_scan_result_path,
  attachment_storage_presigned_url_path,
  attachment_storage_complete_upload_path
} from '~/routes';
import {defaultOpts} from "./fetch";

class UploadManager {
  constructor() {
    if (UploadManager.instance) return UploadManager.instance;

    this.uploads = new Map();
    this.fileTokens = new Map();
    this.csrfToken = this.getCsrfToken();
    this.progIdentifier = this.getProgIdentifier();
    this.globalCallbacks = new Set();
    this.uppyInstances = new Map();
    this.SCAN_STATUSES = {
      SCANNING: 'scanning',
      INFECTED: 'infected',
      SCAN_FAILED: 'scan_failed',
    };

    this.setupPersistence();
    this.restoreFileTokenMappings();
    this.setupFormSubmitDetection();
    UploadManager.instance = this;
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  getProgIdentifier() {
    return document.querySelector('[data-programme-identifier]')?.dataset?.programmeIdentifier;
  }

  generateFileId({name, size, data}) {
    return `${name}_${size}_${data?.lastModified}`;
  }

  getFileTokenMappings() {
    return JSON.parse(localStorage.getItem('file_token_mappings') || '{}');
  }

  getUploadSessions() {
    return JSON.parse(sessionStorage.getItem('upload_sessions') || '[]');
  }

  // ========== PERSISTENCE & Re-HYDRATE ==========
  persistFileTokenMapping(fileId, token, fileName, fieldName, uploadId) {
    const mappings = this.getFileTokenMappings();
    mappings[fileId] = {token, fileName, fieldName, uploadId, status: 'pending', progress: 0};
    localStorage.setItem('file_token_mappings', JSON.stringify(mappings));
  }

  restoreFileTokenMappings() {
    const mappings = this.getFileTokenMappings();
    for (const [fileId, data] of Object.entries(mappings)) {
      this.fileTokens.set(fileId, data.token);
      if (!this.uploads.has(data.token)) {
        this.uploads.set(data.token, {
          token: data.token,
          status: data.status || 'pending',
          progress: data.progress || 0,
          scan_id: data.scan_id,
          fileName: data.fileName,
          fileId: fileId,
          fieldName: data.fieldName,
          uploadId: data.uploadId,
          callbacks: new Map(),
          attachment: null,
          uppy: null
        });
      }
    }
  }

  setupPersistence() {
    const saved = sessionStorage.getItem('upload_sessions');
    if (saved) {
      JSON.parse(saved).forEach(data => this.rehydrate(data));
    }

    document.addEventListener('turbo:before-cache', () => this.persist());
    document.addEventListener('turbo:load', () => {
      this.csrfToken = this.getCsrfToken();
      this.refreshActiveUploads();
    });
    window.addEventListener('beforeunload', () => this.persist());
  }

  persist() {
    const active = Array.from(this.uploads.entries())
      .filter(([_, u]) => ['uploading', 'scanning', 'infected', 'scan_failed'].includes(u.status))
      .map(([token, u]) => ({token, fileId: u.fileId, fileName: u.fileName, fieldName: u.fieldName, uploadId: u.uploadId, progress: u.progress}));
    sessionStorage.setItem('upload_sessions', JSON.stringify(active));

    const mappings = {};
    for (const [fileId, token] of this.fileTokens) {
      const upload = this.uploads.get(token);
      if (upload && upload.status !== 'cancelled') {
        mappings[fileId] = {
          token: token,
          status: upload.status,
          progress: upload.progress,
          scan_id: upload.scan_id,
          fileName: upload.fileName,
          fieldName: upload.fieldName,
          attachment: upload.attachment,
          uploadId: upload.uploadId,
          updatedAt: Date.now(),
        };
      }
    }
    localStorage.setItem('file_token_mappings', JSON.stringify(mappings));
  }

  async getCompletedUpload(token) {
    const url = attachment_status_path({programme_identifier: this.progIdentifier, token, _options: true})
    const response = await fetch(url);
    return response.json();
  }

  async rehydrate(data) {
    const res = await this.getCompletedUpload(data.token);

    if (res.status === 'uploading') {
      this.uploads.set(data.token, {
        token: data.token,
        status: 'uploading',
        progress: res.progress || 0,
        fileName: data.fileName,
        fieldName: data.fieldName,
        uploadId: data.uploadId,
        callbacks: new Map(),
      });
      this.forceGlobalUpdate();
    } else if (res.attachment && !['cancelled', 'completed'].includes(res.status)) {
      this.uploads.set(data.token, {
        token: data.token,
        status: res.status,
        progress: 100,
        scan_id: res.scan_id,
        fileName: data.fileName,
        fieldName: data.fieldName,
        uploadId: data.uploadId,
        attachment: res.attachment,
        callbacks: new Map(),
      });
      if (res.status === this.SCAN_STATUSES.SCANNING) {
        await this.pollVirusScan(data.token, res.scan_id);
        this.forceGlobalUpdate();
      }
    }
  }

  async refreshActiveUploads() {
    const response = await fetch(attachment_active_upload_session_tokens_path({
      programme_identifier: this.progIdentifier,
      _options: true
    }));
    const tokens = await response.json();

    // Remove orphaned completed uploads from UI
    for (const upload of this.getUploadSessions()) {
      if (!tokens.includes(upload.token)) {
        if (upload.fileId) {
          this.fileTokens.delete(upload.fileId);

          const mappings = this.getFileTokenMappings();
          if (mappings[upload.fileId]) {
            delete mappings[upload.fileId];
            localStorage.setItem('file_token_mappings', JSON.stringify(mappings));
          }

          this.uploads.delete(upload.token);
          this.forceGlobalUpdate();
        }
      }
    }

    // Re-hydrate uploads
    for (const token of tokens) {
      if (!this.uploads.has(token)) {
        await this.rehydrate({token});
      }
    }
  }

  setupFormSubmitDetection() {
    // Listen for clicks on your submit buttons
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.submit-button, .save-draft-button, .save-and-exit-button');
      if (button) {
        const form = document.querySelector('.adalia-form');

        if (form) {
          const uploadFields = form.querySelectorAll('[data-upload-field]');
          uploadFields.forEach(field => {
            const fieldName = field.getAttribute('data-upload-field');
            if (fieldName) {
              const uploadId = field.getAttribute('data-upload-id');
              this.clearFieldUploads(uploadId, fieldName);
            }
          })
        }
      }
    })
  }

  async clearFieldUploads(uploadId, fieldName) {
    for (const [token, upload] of this.uploads) {
      if (upload.uploadId === uploadId) {
        if (upload.fieldName === fieldName && ['done', 'scan_failed', 'infected'].includes(upload.status)) {
          const res = await (await this.updateScanStatus(token, 'completed')).json();
          if (res.status === 'completed') {
            const upload = this.uploads.get(token);

            if (upload.fileId) {
              this.fileTokens.delete(upload.fileId);
              const mappings = this.getFileTokenMappings();
              if (mappings[upload.fileId]) {
                delete mappings[upload.fileId];
                localStorage.setItem('file_token_mappings', JSON.stringify(mappings));
              }
            }
            this.uploads.delete(token);
          }
        }
      }
    }
    this.forceGlobalUpdate();
  }

  // ========== TOKEN MANAGEMENT ==========
  async getOrCreateTokenForFile(file, uploadId, fieldName) {
    const fileId = this.generateFileId(file);
    const existingToken = this.fileTokens.get(fileId);

    if (existingToken) {
      const existingUpload = this.uploads.get(existingToken);
      if (existingUpload) {
        if (existingUpload.status === 'done' && existingUpload.attachment) {
          return {token: existingToken, existing: true, fileId, attachment: existingUpload.attachment};
        }
        if (existingUpload.status !== 'done') {
          return {token: existingToken, existing: true, fileId};
        }
      }
    }

    const response = await fetch(attachment_create_upload_token_path({programme_identifier: this.progIdentifier}), {
      method: 'POST',
      headers: {'X-CSRF-Token': this.csrfToken, 'Content-Type': 'application/json'},
      body: JSON.stringify({fieldName, filename: file.name, fileSize: file.size})
    });

    const {token} = await response.json();

    this.fileTokens.set(fileId, token);
    this.persistFileTokenMapping(fileId, token, file.name, fieldName, uploadId);

    this.uploads.set(token, {
      token: token,
      status: 'uploading',
      progress: 0,
      fileName: file.name,
      fileId: fileId,
      fieldName: fieldName,
      uploadId: uploadId,
      callbacks: new Map(),
      attachment: null,
      uppy: null
    });

    this.forceGlobalUpdate();
    return {token: token, existing: false, fileId: fileId};
  }

  // ========== UPPY UPLOAD INSTANCE ==========
  getUppyId(uploadId, name) {
    return `uppy-${name}-${uploadId}`;
  }

  getUppyForComponent(name, uploadId) {
    return globalUppyInstances.get(`uppy-${name}-${uploadId}`) || null;
  }

  loadUppyLocale = (locale) => {
    const locales = {
      fi: () => import('@uppy/locales/lib/fi_FI'),
      fr: () => import('@uppy/locales/lib/fr_FR'),
      sv: () => import('@uppy/locales/lib/sv_SE'),
    };
    return locales[locale]?.() || null;
  };

  async getUppyInstance(uppyId, options = {}) {
    // Check if instance exists
    if (globalUppyInstances.has(uppyId)) {
      const existingUppy = globalUppyInstances.get(uppyId);
      const files = existingUppy.getFiles();
      const hasUnfinishedFiles = files.some(f => !f.progress.uploadComplete && !f.progress.uploadStarted && !f.error);

      if (hasUnfinishedFiles) {
        existingUppy.upload().catch(err => console.warn("Resume failed:", err));
      }
      return existingUppy;
    }

    // Only create new instance if one does not exist
    const UppyModule = await import('@uppy/core');
    const TusModule = await import('@uppy/tus');
    const AwsS3Module = await import('@uppy/aws-s3');

    const maxNumberOfFiles = options.maxNumberOfFiles || 10;
    const uppyLocale = (await this.loadUppyLocale(I18n.locale))?.default || {};

    const uppy = new UppyModule.default({
      id: uppyId,
      autoProceed: false,
      restrictions: {
        maxFileSize: options.maxFileSize || 100 * 1024 * 1024,
        maxNumberOfFiles: maxNumberOfFiles,
        allowedFileTypes: options.allowedTypes || null
      },
      locale: {
        ...uppyLocale,
        strings: {
          ...(uppyLocale?.strings||{}),
          dropPasteFiles: I18n.t(maxNumberOfFiles > 1 ? 'drop_paste_files' : 'drop_paste_file', {scope: 'uppy'}),
          browseFiles: I18n.t(maxNumberOfFiles > 1 ? 'browse_files' : 'browse_file', {scope: 'uppy'})
        },
      },
    });

    if (this.useS3CompatibleStorage()) {
      // S3 DIRECT UPLOAD - NO SERVER BANDWIDTH
      uppy.use(AwsS3Module.default, {
        getUploadParameters: async (file) => {
          const response = await fetch(
            `${attachment_storage_presigned_url_path({programme_identifier: this.progIdentifier})}?${new URLSearchParams({
              filename: file.name,
              content_type: file.type || "application/octet-stream",
            })}`
          );

          const result = await response.json();
          if (result) {
            return {
              method: result.method,
              url: result.url,
              headers: result.headers || {},
              fields: result.fields || {},
            };
          } else {
            throw new Error('Failed to get upload URL');
          }
        },
        timeout: 3600000, // 1 hour for large files
        limit: 5, // 5 concurrent uploads
      });

    } else {
      // TUS UPLOAD - USES LOCAL SERVER
      uppy.use(TusModule.default, {
        endpoint: '/files',
        resume: true,
        storeFingerprintForResuming: true,
        retryDelays: null,
        chunkSize: 5 * 1024 * 1024,
        fingerprint: (file) => {
          return ['tus', file.name, file.type, file.size].join('-');
        }
      });
    }

    uppy.on('file-added', async (file) => {
      const f = await options.onFileAdded?.(file);
      const token = f.meta.token;

      // Store the token and storage type as metadata on the file for later reference
      uppy.setFileMeta(file.id, {
        token,
        storageType: uploadManager.useS3CompatibleStorage() ? 's3' : 'local'
      });

      // Start uploading the file
      uppy.upload();

      const currentUpload = this.uploads.get(token);
      if (currentUpload) {
        currentUpload.uppyFileId = file.id;
      }

      this.forceGlobalUpdate();
    });

    uppy.on('upload-progress', (file, progress) => {
      let percentage = 0;
      if (progress.bytesTotal > 0) {
        percentage = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100);
      }

      const currentUpload = this.uploads.get(file.meta?.token);
      if (currentUpload) {
        currentUpload.progress = percentage;
        currentUpload.status = 'uploading';
        this.forceGlobalUpdate();
      }

      options.onProgress?.(percentage);
    });

    uppy.on('upload-success', async (file, response) => {
      file.progress.uploadComplete = true;
      file.complete = true;
      if (options.onUploadSuccess) {
        await options.onUploadSuccess(file, response);
      }
    });

    uppy.on('file-removed', (file) => {
      this.cancelUpload(file.meta?.token, uppy);
    });

    uppy.on('upload-error', (file, error) => {
      const currentUpload = this.uploads.get(file.meta?.token);
      if (currentUpload) {
        currentUpload.status = 'failed';
        this.forceGlobalUpdate();
      }
    });

    // uppy.on('complete', () => {
    //   uppy.cancelAll();
    // });

    uppy.on('error', (error) => {
      options.onError?.(error);
    });

    // Store it globally before returning
    globalUppyInstances.set(uppyId, uppy);
    return uppy;
  }

  deleteUppyInstance(uppyId) {
    const uppy = this.uppyInstances.get(uppyId);
    if (uppy) {
      try {
        uppy.cancelAll();
        uppy.close();
      } catch (e) {
      }
      this.uppyInstances.delete(uppyId);
    }
  }

  // ========== UPLOAD FINALIZATION ==========
  async finalizeUpload(file, response, permissions = {}) {
    const token = file.meta?.token;
    const upload = this.uploads.get(token);
    if (!upload) return;

    const {uploadURL} = response;

    let url;
    let url_options;

    if (file.meta.storageType === 's3') {
      url = attachment_storage_complete_upload_path({programme_identifier: this.progIdentifier});
      url_options = {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          attachment: {
            id: new URL(uploadURL).pathname.slice(1),
            metadata: {
              filename: file.name,
              mime_type: file.type,
              size: file.size,
            }
          },
          permissions: permissions,
        })
      }
    } else {
      url = attachment_finalize_path({programme_identifier: this.progIdentifier});
      url_options = {
        ...defaultOpts(),
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          attachment: {
            id: uploadURL.substring(uploadURL.indexOf('files/')),
            storage: "cache",
            metadata: {
              filename: file.name,
              size: file.size,
              mime_type: file.type,
            }
          },
          permissions: permissions,
        })
      };
    }

    const finalizeResponse = await fetch(url, url_options);
    const result = await finalizeResponse.json();
    if (result) {
      upload.attachment = result;
      upload.progress = 100;

      if (result.status === this.SCAN_STATUSES.SCANNING) {
        upload.status = result.status;
        upload.scan_id = result.scan_id;
        this.notifyCallbacks(token, upload.status, {attachment: result});
        await this.pollVirusScan(token, upload.scan_id);
        this.forceGlobalUpdate();
      } else {
        upload.status = 'done';
        this.notifyCallbacks(token, upload.status, {attachment: result});
        this.forceGlobalUpdate();

        const uppy = this.getUppyForComponent(upload.fieldName, upload.uploadId);
        uppy.emit('done', upload);

        // setTimeout(() => {
        //   uppy.removeFile(file.id);
        //   globalUppyInstances.delete(uppyId);
        // }, 1000);
      }
    }

    return result;
  }

  // ========== POLL VIRUS SCAN STATUS ==========
  async pollVirusScan(token, scan_id) {
    let attempts = 0;
    const maxAttempts = 720; // 1 hour (720 * 5 seconds)

    const poll = async () => {
      const upload = this.uploads.get(token);
      if (!upload || upload.status !== this.SCAN_STATUSES.SCANNING) return;

      try {
        const response = await fetch(virus_scanner_get_scan_result_path({
          programme_identifier: this.progIdentifier,
          scan_id
        }), {
          headers: {'X-CSRF-Token': this.csrfToken}
        });
        const result = await response.json();

        if (result.result) {
          const res = await (await this.updateScanStatus(token, result.result === 'OK' ? 'done' : this.SCAN_STATUSES.INFECTED)).json();
          if (res.status) {
            upload.status = res.status;
            this.notifyCallbacks(token, res.status, {attachment: upload.attachment});
            this.forceGlobalUpdate();
            return true;
          }
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          const res = await (await this.updateScanStatus(token, this.SCAN_STATUSES.SCAN_FAILED)).json();
          if (res.status) {
            upload.status = res.status;
            this.notifyCallbacks(token, res.status, {error: new Error('Virus scan timeout')});
            this.forceGlobalUpdate();
            return true;
          }
        }
      } catch (error) {
        console.error(error);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 5000);
        }
      }
    };

    poll();
  }

  updateScanStatus(token, status) {
    return fetch(attachment_update_status_path({programme_identifier: this.progIdentifier, token}), {
      ...defaultOpts(),
      method: 'POST',
      headers: {
        'X-CSRF-Token': this.csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({token, status})
    });
  }

  async getPendingUploads(uploadId, fieldName, statuses = []) {
    const pendingUploads = [];
    const mappings = this.getFileTokenMappings();

    for (const [fileId, data] of Object.entries(mappings)) {
      if (data.uploadId === uploadId) {
        // Only restore uploads for this component's fieldName
        if (data.fieldName === fieldName || statuses.includes(data.status)) {
          const upload = this.uploads.get(data.token);

          if (upload?.attachment) {
            pendingUploads.push({
              fileId: fileId,
              token: data.token,
              progress: 100,
              fileName: data.fileName,
              ...upload.attachment,
              status: upload?.status,
            });
          } else if (upload?.status === 'uploading') {
            pendingUploads.push({
              fileId: fileId,
              token: data.token,
              status: 'uploading',
              progress: upload.progress || 0,
              fileName: data.fileName,
            });
          } else if (data.status === 'done') {
            // Check server for completed file
            const res = await this.getCompletedUpload(data.token);
            if (res.attachment) {
              pendingUploads.push({
                fileId: fileId,
                token: data.token,
                progress: 100,
                fileName: data.fileName,
                ...res.attachment,
                status: 'done',
              });
            }
          }
        }
      }
    }
    return pendingUploads;
  }

  getActiveUploads = (uploadId) => {
    return uploadManager.getPendingUploads(uploadId, null, ['uploading', 'scanning'])
  };

  // Cancel an active upload by token, abort the Uppy upload if provided
  async cancelUpload(token, uppy = null) {
    const upload = this.uploads.get(token);
    if (!upload) return false;

    const res = await (await this.updateScanStatus(token, 'cancelled')).json();
    if (res.status === 'cancelled') {
      // Cancel upload if in progress
      uppy ||= this.getUppyForComponent(upload.fieldName, upload.uploadId)
      if (uppy) {
        try {
          //uppy.cancelAll();
          uppy.removeFile(upload.uppyFileId);
        } catch (e) {
        }
      }

      // Clean up file mappings, and remove from tracking
      if (upload.fileId) {
        this.fileTokens.delete(upload.fileId);
        const mappings = this.getFileTokenMappings();
        if (mappings[upload.fileId]) {
          delete mappings[upload.fileId];
          localStorage.setItem('file_token_mappings', JSON.stringify(mappings));
        }
      }

      this.uploads.delete(token);
      this.forceGlobalUpdate();
      return true;
    }
  }

  retryUpload(upload) {
    const uppy = this.getUppyForComponent(upload.fieldName, upload.uploadId);
    const file = uppy.getFile(upload.uppyFileId);
    if (!file) return;

    if (file.error) {
      uppy.retryUpload(file.id);
    } else if (file.isPaused) {
      uppy.pauseResume(file.id);
    }
  }

  // Register an upload specific callback that immediately receives the current upload state
  onUpdate(token, uppyId, callback) {
    const upload = this.uploads.get(token);
    if (upload) {
      upload.callbacks.set(token, callback);
      callback({status: upload.status, progress: upload.progress, attachment: upload.attachment});
    }
    return () => this.offUpdate(token, uppyId);
  }

  // Remove a specific upload callback and clean up teh uppy instance if no callbacks remain
  async offUpdate(token, uppyId) {
    const upload = this.uploads.get(token);
    if (!upload) return;

    upload.callbacks.delete(token);

    if (upload.callbacks.size === 0) {
      this.deleteUppyInstance(uppyId)
    }
  }

  // Notify all registered callbacks for a given upload about status changes
  notifyCallbacks(token, status, data) {
    const upload = this.uploads.get(token);
    if (upload) {
      upload.callbacks.forEach(cb => cb({status, ...data}));
    }
  }

  // Register a global update listener that triggers immediately
  onGlobalUpdate(callback) {
    this.globalCallbacks.add(callback);
    this.forceGlobalUpdate();
    return () => this.globalCallbacks.delete(callback);
  }

  // Force a global update to reflect the new file and its upload status
  forceGlobalUpdate() {
    const updates = [];
    for (const [token, u] of this.uploads) {
      // Remove the upload if its Uppy file no longer exists
      const uppy = this.getUppyForComponent(u.fieldName, u.uploadId);
      if (!uppy || (u.uppyFileId && !uppy.getFiles().find(file => file.id === u.uppyFileId))) {
        this.cancelUpload(token);
      }

      // Add active and scan uploads to the global update
      const isScanStatus = Object.values(this.SCAN_STATUSES).includes(u.status);
      const isActiveStatus = ['uploading', 'failed'].includes(u.status);
      if (isActiveStatus || isScanStatus) {
        updates.push({
          token,
          status: u.status,
          progress: isScanStatus ? 100 : u.progress || 0,
          uppyFileId: u.uppyFileId,
          fieldName: u.fieldName,
          uploadId: u.uploadId,
          fileName: u.fileName || 'Uploading...',
          attachment: u.attachment,
        });
      }
    }
    this.globalCallbacks.forEach(cb => {
      try {
        cb([...updates]);
      } catch (e) {
      }
    });
  }

  notifyGlobal() {
    this.forceGlobalUpdate();
  }

  // Checks if updated attachment component is enabled
  useUpdatedAttachmentComponent() {
    return document.querySelector('[data-use-updated-attachment-component]')
      ?.dataset?.useUpdatedAttachmentComponent === "true";
  }

  // Checks whether S3-compatible storage is enabled
  useS3CompatibleStorage() {
    return document.querySelector('[data-use-s3-compatible-storage]')
      ?.dataset?.useS3CompatibleStorage === "true";
  }
}

const uploadManager = new UploadManager();
export default uploadManager;

if (typeof window !== 'undefined') {
  window.uploadManager = uploadManager;
}
